import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@mui/material";

import { useEffect, useState } from "react";

import type {
    AuditQuery,
    AuditRecordDetail,
    AuditRecordList,
    PagedResult
} from "../../models/Audit";

import AuditDetailDrawer from "./AuditDetailDrawer";

import {
    getAuditActionLabel,
    getAuditEntityLabel
} from "../../functions/auditHelpers";

import useAuthToken from '../../functions/useAuthToken';
import { useApiClient } from "../useApiClient";
import { access } from "fs";

interface Props {
    rootEntityType?: string;
    rootEntityId?: string;
}

export default function AuditLog({
    rootEntityType,
    rootEntityId
}: Props) {

    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    const [data, setData] =
        useState<PagedResult<AuditRecordList> | null>(null);

    const [loading, setLoading] =
        useState(false);

    const [selectedRecord, setSelectedRecord] =
        useState<AuditRecordDetail | null>(null);

    const [detailLoading, setDetailLoading] =
        useState(false);

    const [drawerOpen, setDrawerOpen] =
        useState(false);

    const [entityType, setEntityType] =
        useState("");

    const [action, setAction] =
        useState("");

    const [fromDate, setFromDate] =
        useState("");

    const [toDate, setToDate] =
        useState("");

    const [page, setPage] =
        useState(0);

    const [pageSize, setPageSize] =
        useState(25);

    useEffect(() => {
        loadAudit();
    }, [
        page,
        pageSize,
        entityType,
        action,
        fromDate,
        toDate,
        rootEntityType,
        rootEntityId
    ]);

    async function loadAudit() {

        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Invalid access token in getDiaryRange');

        try {
            setLoading(true);

            const query: AuditQuery = {
                page: page + 1,
                pageSize,
                rootEntityType,
                rootEntityId,
                entityType:
                    entityType || undefined,
                action:
                    action || undefined
            };

            if (fromDate) {
                query.fromUtc =
                    new Date(
                        `${fromDate}T00:00:00`
                    ).toISOString();
            }

            if (toDate) {
                const date =
                    new Date(
                        `${toDate}T23:59:59.999`
                    );

                query.toUtc =
                    date.toISOString();
            }

            const params =
                new URLSearchParams();

            Object.entries(query)
                .forEach(([key, value]) => {
                    if (
                        value !== undefined &&
                        value !== null &&
                        value !== ""
                    ) {
                        params.append(
                            key,
                            String(value)
                        );
                    }
                });

            const response =
                await get<PagedResult<AuditRecordList>>(`audit?${params.toString()}`, accessToken);

            setData(response);
        }
        finally {
            setLoading(false);
        }
    }

    async function openRecord(id: number) {

        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Invalid access token in getDiaryRange');

        try {
            setDrawerOpen(true);
            setDetailLoading(true);
            setSelectedRecord(null);

            const response =
                await get<AuditRecordDetail>(`audit/${id}`, accessToken);

            setSelectedRecord(response);
        }
        finally {
            setDetailLoading(false);
        }
    }

    function clearFilters() {
        setEntityType("");
        setAction("");
        setFromDate("");
        setToDate("");
        setPage(0);
    }

    return (
        <>
            <Paper sx={{ p: 2 }}>
                <Stack
                    direction={{
                        xs: "column",
                        md: "row"
                    }}
                    spacing={2}
                    mb={2}
                >
                    {!rootEntityType && (
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: 180
                            }}
                        >
                            <InputLabel>
                                Entity
                            </InputLabel>

                            <Select
                                value={entityType}
                                label="Entity"
                                onChange={e => {
                                    setEntityType(
                                        e.target.value
                                    );
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="">
                                    All
                                </MenuItem>

                                <MenuItem value="Artwork">
                                    Artwork
                                </MenuItem>

                                <MenuItem value="ArtworkReference">
                                    Artwork Reference
                                </MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <FormControl
                        size="small"
                        sx={{
                            minWidth: 200
                        }}
                    >
                        <InputLabel>
                            Activity
                        </InputLabel>

                        <Select
                            value={action}
                            label="Activity"
                            onChange={e => {
                                setAction(
                                    e.target.value
                                );
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                All
                            </MenuItem>

                            <MenuItem value="ArtworkUpdated">
                                Artwork updated
                            </MenuItem>

                            <MenuItem value="ProvenanceUpdated">
                                Provenance updated
                            </MenuItem>

                            <MenuItem value="ReferenceCreated">
                                Reference added
                            </MenuItem>

                            <MenuItem value="ReferenceUpdated">
                                Reference updated
                            </MenuItem>

                            <MenuItem value="ReferenceDeleted">
                                Reference deleted
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        label="From"
                        type="date"
                        value={fromDate}
                        onChange={e => {
                            setFromDate(
                                e.target.value
                            );
                            setPage(0);
                        }}
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />

                    <TextField
                        size="small"
                        label="To"
                        type="date"
                        value={toDate}
                        onChange={e => {
                            setToDate(
                                e.target.value
                            );
                            setPage(0);
                        }}
                        slotProps={{
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />

                    <Button
                        onClick={clearFilters}
                    >
                        Clear
                    </Button>
                </Stack>

                {loading ? (
                    <Box
                        py={6}
                        display="flex"
                        justifyContent="center"
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        Date / Time
                                    </TableCell>

                                    <TableCell>
                                        User
                                    </TableCell>

                                    <TableCell>
                                        Activity
                                    </TableCell>

                                    <TableCell>
                                        Entity
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data?.items.map(record => (
                                    <TableRow
                                        key={record.id}
                                        hover
                                        onClick={() =>
                                            openRecord(
                                                record.id
                                            )
                                        }
                                        sx={{
                                            cursor: "pointer"
                                        }}
                                    >
                                        <TableCell>
                                            {new Date(
                                                record.createdUtc
                                            ).toLocaleString(
                                                "en-GB"
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            <Typography>
                                                {
                                                    record.userDisplayName ||
                                                    record.userEmail ||
                                                    "System"
                                                }
                                            </Typography>

                                            {record.userDisplayName &&
                                                record.userEmail && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {
                                                            record.userEmail
                                                        }
                                                    </Typography>
                                                )}
                                        </TableCell>

                                        <TableCell>
                                            {getAuditActionLabel(
                                                record.action
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            {getAuditEntityLabel(
                                                record.entityType,
                                                record.entityId,
                                                record.rootEntityType,
                                                record.rootEntityId
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {data?.items.length === 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                        >
                                            No audit records found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div"
                    count={data?.totalCount ?? 0}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={[
                        10,
                        25,
                        50,
                        100
                    ]}
                    onPageChange={(_, newPage) =>
                        setPage(newPage)
                    }
                    onRowsPerPageChange={e => {
                        setPageSize(
                            Number(e.target.value)
                        );
                        setPage(0);
                    }}
                />
            </Paper>

            <AuditDetailDrawer
                open={drawerOpen}
                record={selectedRecord}
                loading={detailLoading}
                onClose={() =>
                    setDrawerOpen(false)
                }
            />
        </>
    );
}